"use client";
import { useState, useCallback, useEffect } from "react";

import { selectedWorkspaceSlugCookie } from "@stadium/helpers/cookie";

/**
 * Persists the user's selected workspace slug in a cookie (readable by middleware and auth).
 * Prefer over sessionStorage so the value survives navigation patterns that omit the path segment.
 */
const useSelectedWorkspaceSlugCookie = () => {
  const [storedValue, setStoredValue] = useState<string | undefined>(undefined);

  useEffect(() => {
    const v = selectedWorkspaceSlugCookie.get();
    if (v != null) setStoredValue(v);
  }, []);

  const setValue = useCallback((newValue: string | undefined) => {
    try {
      if (newValue === undefined) {
        selectedWorkspaceSlugCookie.reset();
      } else {
        selectedWorkspaceSlugCookie.set(newValue);
      }
    } catch {}
    setStoredValue(newValue);
  }, []);

  const deleteItem = useCallback(() => {
    selectedWorkspaceSlugCookie.reset();
    setStoredValue(undefined);
  }, []);

  return [storedValue, setValue, deleteItem] as [
    string | undefined,
    typeof setValue,
    typeof deleteItem,
  ];
};

export default useSelectedWorkspaceSlugCookie;
